/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {
    Component, ElementRef, HostBinding,
    HostListener, OnDestroy, OnInit
} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {EditorView} from 'prosemirror-view';
import {Subscription} from 'rxjs';
import {FcAgEditorService} from '../../../editor.service';
import {MenuService} from '../menu.service';
import Icon from '../../../icons';
import {Link as LinkCommand} from '../MenuCommands';

/**
 * Link component.
 */
@Component({
    selector: 'fc-ag-link',
    templateUrl: './link.component.html',
    styleUrls: ['./link.component.scss']
})

export class LinkComponent implements OnInit, OnDestroy {
    /**
     * Show popup.
     */
    showPopup = false;

    /**
     * Is active.
     */
    isActive = false;

    private canExecute = true;

    private editorView: EditorView;

    private updateSubscription: Subscription;

    /**
     * Form instance.
     */
    form = new FormGroup({
        href: new FormControl('', [
            Validators.required,
            Validators.pattern('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/??([^#\n\r]*)?#?([^\n\r]*)')
        ]),
        text: new FormControl('', [Validators.required]),
        openInNewTab: new FormControl(true)
    });

    /**
     * Default constructor.
     * 
     * @param el Element reference.
     * @param fcAgEditorService FcAgEditorService.
     * @param menuService Menu service.
     */
    constructor(
        private el: ElementRef,
        private fcAgEditorService: FcAgEditorService,
        private menuService: MenuService
    ) {
    }

    /**
     * Valid binding.
     * 
     * @returns Boolean.
     */
    @HostBinding('class.FcAgEditor__MenuItem--Active') get valid(): boolean {
        return this.isActive || this.showPopup;
    }

    /**
     * Disabled binding.
     * 
     * @returns Boolean.
     */
    @HostBinding('class.FcAgEditor--Disabled') get disabled(): boolean {
        return !this.canExecute;
    }

    /**
     * Getter for icon.
     * 
     * @returns String.
     */
    get icon(): string {
        return Icon.get(this.isActive ? 'unlink' : 'link') || '';
    }

    /**
     * Getter for title.
     * 
     * @returns String.
     */
    get title(): string {
        return this.fcAgEditorService.locals.get(this.isActive ? 'removeLink' : 'insertLink');
    }

    /**
     * Getter for href.
     * 
     * @returns String.
     */
    get href(): any {
        return this.form.get('href');
    }

    /**
     * Getter for text.
     * 
     * @returns String.
     */
    get text(): any {
        return this.form.get('text');
    }

    /**
     * On document click binding.
     * 
     * @param e Mouse event.
     */
    @HostListener('document:mousedown', ['$event']) onDocumentClick(e: MouseEvent): void {
        if (!this.el.nativeElement.contains(e.target) && this.showPopup) {
            this.hideForm();
        }
    }

    /**
     * Get label.
     * 
     * @param key String.
     * @returns String.
     */
    getLabel(key: string): string {
        return this.fcAgEditorService.locals.get(key);
    }

    private hideForm(): void {
        this.showPopup = false;
        this.form.reset({
            href: '',
            text: '',
            openInNewTab: true
        });
        this.text.enable();
    }

    /**
     * On mouse down callback.
     * 
     * @param e Mouse event.
     */
    onMouseDown(e: MouseEvent): void {
        if (e.button !== 0) {
            return;
        }

        const {state, dispatch} = this.editorView;

        if (this.isActive) {
            LinkCommand.remove(state, dispatch);
            return;
        }

        this.showPopup = !this.showPopup;
        if (this.showPopup) {
            this.setText();
        }
    }

    private setText = () => {
        const {state: {selection, doc}} = this.editorView;
        const {empty, from, to} = selection;
        const selectedText = !empty ? doc.textBetween(from, to) : '';

        if (selectedText) {
            this.text.patchValue(selectedText);
            this.text.disable();
        }
    }

    private update = (view: EditorView) => {
        const {state} = view;
        this.isActive = LinkCommand.isActive(state, {strict: false});
        this.canExecute = LinkCommand.canExecute(state);
    }

    /**
     * Insert link.
     * 
     * @param e Mouse event.
     */
    insertLink(e: MouseEvent): void {
        e.preventDefault();
        const {text, href, openInNewTab} = this.form.getRawValue();
        const {dispatch, state} = this.editorView;
        const {selection} = state;

        const attrs = {
            title: href,
            href,
            target: openInNewTab ? '_blank' : '_self'
        };

        if (selection.empty) {
            LinkCommand.insert(text, attrs)(state, dispatch);
            this.editorView.focus();
        } else {
            LinkCommand.update(attrs)(state, dispatch);
        }
        this.hideForm();
    }

    /**
     * Angular life cycle.
     */
    ngOnInit(): void {
        this.editorView = this.menuService.editor.view;

        this.updateSubscription = this.menuService.editor.update.subscribe((view: EditorView) => {
            this.update(view);
        });
    }

    /**
     * Angular life cycle.
     */
    ngOnDestroy(): void {
        this.updateSubscription.unsubscribe();
    }
}
